import * as THREE from 'three';
import { PhysicsBody } from '../types';

/**
 * Projectile/Missile that can be fired from the ship.
 * Obeys physics simulation (gravity, collisions).
 * Optimized for performance.
 */
export class Projectile {
  public readonly id: string;
  public readonly mesh: THREE.Group;
  public physicsBody: PhysicsBody;

  // Projectile properties
  private readonly speed: number = 150;
  private readonly lifetime: number = 10; // Reduced lifetime
  private age: number = 0;
  private alive: boolean = true;

  // Visual elements - simplified for performance
  private coreMesh: THREE.Mesh;

  // Trail - pre-allocated buffer
  private trail: THREE.Line;
  private trailPositions: Float32Array;
  private trailIndex: number = 0;
  private readonly maxTrailPoints: number = 30; // Reduced trail length
  private trailUpdateCounter: number = 0;

  constructor(
    position: THREE.Vector3,
    direction: THREE.Vector3,
    inheritedVelocity: THREE.Vector3 = new THREE.Vector3()
  ) {
    this.id = `projectile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create container group
    this.mesh = new THREE.Group();
    this.mesh.name = this.id;

    // Simplified projectile - single mesh with glow effect
    const coreGeometry = new THREE.SphereGeometry(1, 8, 6); // Reduced segments
    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0xff4400,
    });
    this.coreMesh = new THREE.Mesh(coreGeometry, coreMaterial);
    this.mesh.add(this.coreMesh);

    // Simple streak behind - no separate glow mesh
    const streakGeometry = new THREE.ConeGeometry(0.5, 3, 6);
    const streakMaterial = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 0.6,
    });
    const streak = new THREE.Mesh(streakGeometry, streakMaterial);
    streak.rotation.x = -Math.PI / 2;
    streak.position.z = -1.5;
    this.mesh.add(streak);

    // NO point light - too expensive with many projectiles

    // Pre-allocate trail buffer
    this.trailPositions = new Float32Array(this.maxTrailPoints * 3);
    for (let i = 0; i < this.maxTrailPoints; i++) {
      this.trailPositions[i * 3] = position.x;
      this.trailPositions[i * 3 + 1] = position.y;
      this.trailPositions[i * 3 + 2] = position.z;
    }

    const trailGeometry = new THREE.BufferGeometry();
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(this.trailPositions, 3));
    trailGeometry.setDrawRange(0, 0);

    const trailMaterial = new THREE.LineBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.5,
    });
    this.trail = new THREE.Line(trailGeometry, trailMaterial);

    // Set initial position
    this.mesh.position.copy(position);

    // Orient mesh to face direction of travel
    const lookTarget = position.clone().add(direction);
    this.mesh.lookAt(lookTarget);

    // Calculate velocity
    const projectileVelocity = direction.clone().normalize().multiplyScalar(this.speed);
    const totalVelocity = inheritedVelocity.clone().add(projectileVelocity);

    // Create physics body
    this.physicsBody = {
      id: this.id,
      position: position.clone(),
      velocity: totalVelocity,
      acceleration: new THREE.Vector3(),
      mass: 0.1,
      radius: 1,
      isStatic: false,
      object3D: this.mesh,
    };
  }

  public update(deltaTime: number): void {
    if (!this.alive) return;

    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.alive = false;
      return;
    }

    // Update trail every 3rd frame for performance
    this.trailUpdateCounter++;
    if (this.trailUpdateCounter >= 3) {
      this.trailUpdateCounter = 0;
      this.updateTrail();
    }

    // Orient mesh to face direction of travel
    if (this.physicsBody.velocity.lengthSq() > 0.1) {
      const lookTarget = this.mesh.position.clone().add(this.physicsBody.velocity.clone().normalize());
      this.mesh.lookAt(lookTarget);
    }

    // Fade out near end of life
    const remainingLife = this.lifetime - this.age;
    if (remainingLife < 1) {
      const fade = remainingLife;
      (this.coreMesh.material as THREE.MeshBasicMaterial).opacity = fade;
    }
  }

  private updateTrail(): void {
    // Shift all positions back and add new position at front
    const pos = this.mesh.position;

    // Shift existing points
    for (let i = this.maxTrailPoints - 1; i > 0; i--) {
      this.trailPositions[i * 3] = this.trailPositions[(i - 1) * 3];
      this.trailPositions[i * 3 + 1] = this.trailPositions[(i - 1) * 3 + 1];
      this.trailPositions[i * 3 + 2] = this.trailPositions[(i - 1) * 3 + 2];
    }

    // Add current position at front
    this.trailPositions[0] = pos.x;
    this.trailPositions[1] = pos.y;
    this.trailPositions[2] = pos.z;

    // Update draw range
    this.trailIndex = Math.min(this.trailIndex + 1, this.maxTrailPoints);
    this.trail.geometry.setDrawRange(0, this.trailIndex);

    // Mark for update
    (this.trail.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
  }

  public isAlive(): boolean {
    return this.alive;
  }

  public kill(): void {
    this.alive = false;
  }

  public getTrail(): THREE.Line {
    return this.trail;
  }

  public dispose(): void {
    this.trail.geometry.dispose();
    (this.trail.material as THREE.Material).dispose();

    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
